import { DateTime } from "luxon";
import { WorkflowActionLink } from "./workflow.model";
import { AccountType, ApplicationUserType, CommonBaseModel } from "./common.model";
import { IExternalUserDetails } from "./security.model";
import { hasValue, pascalCase, tryParseDate, trySetProp } from "../global.functions";
import { cloneDeep, isObject } from "lodash";
import '../global.extensions';
import { iApplyFormControl } from "./control.model";
import { CRC32 } from "../crc32";
import { DataContainer } from "./control.interface";

/** An iApply Application object with metadata. */
export class Application extends CommonBaseModel implements IApplication {
    static OFFLINE_PREFIX = "offline-";

    id!: string | undefined;
    /** Gets or sets the application version number. */
    version!: number;
    /** Gets or sets the form id. */
    formId!: string | undefined;
    /** Gets or sets the form version. */
    formVersion!: number | undefined;
    /** Gets or sets the form organisation. */
    formOrganisationId!: string | undefined;
    /** Gets or sets the organisation id. */
    organisationId!: string | undefined;
    /** Gets or sets the organisation name. */
    organisationName: string | undefined;
    /** Gets or sets the application id for display. */
    applicationIdDisplay!: string | undefined;
    /** Gets or sets the application status. */
    applicationState!: string | undefined;
    /** Gets or sets the application assignee (user id). */
    assignedTo: Assignee[] | undefined = [];
    /** Creation time of this entity. */
    created!: DateTime | undefined;
    /** Creator of this entity. */
    createdBy!: CreatedApplicationUser;
    /** Last modification date of this entity. */
    modified!: DateTime | undefined;
    /** Last modifier user of this entity. */
    modifiedBy!: string | undefined;
    /** Gets or sets the date/time the application transitioned to the current state. */
    stateTransitionDate!: DateTime;
    /** Gets or sets the current state repeat counter.
     * Use 1 as default for backward compatibility with existing applications.*/
    stateRepeatCount: number | undefined = 1;
    /** Gets or sets a value indicating whether the application is a draft. */
    draft!: boolean;
    /** Gets or sets a value indicating whether an application should be tied to a
     * specific version of a form's metadata. */
    useFormVersion!: boolean;
    createdByWorkflowAction!: WorkflowActionLink;
    /** Gets or sets the concurrent repeater editing field. */
    concurrentRepeaterEditingField!: string | undefined;
    /** Application Data! */
    data!: any;
    // data!: DataDictionary;

    /** Is the application currently stored offline? */
    isOffline: boolean = false;
    isDirty: boolean = false; // "true" if saved offline. Indicates the application requires synchronisation with the server.
    offlineApplicationId?: string; // Set when offline...
    offlineEventType: string | undefined;

    /** TAGS (new for 26.2) */
    tags: string[] = [];

    get assigneeDisplayName(): string {
        // TODO: When there are 3 or more assignees, make the display name "John Smith (and 2 more...)"
        // with a tooltip icon to show the full list.
        if (this.assignedTo && this.assignedTo.length > 0) {
            return this.assignedTo.map(assignee => !!assignee.id ? assignee.name : 'anonymous').join(', ');
        }

        return '';
    }

    constructor(data?: IApplication) {
        super();

        // initialize "real" offline app id.
        this.offlineApplicationId = crypto.randomUUID();

        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any): this {
        super.init(_data);

        if (_data) {
            if (Array.isArray(_data["assignedTo"])) {
                this.assignedTo = _data["assignedTo"].map(item => Assignee.fromJS(item));
            }

            this.created = tryParseDate(_data["created"]);
            this.createdBy = _data["createdBy"] ? (new CreatedApplicationUser()).init(_data["createdBy"]) : <any>undefined;
            this.modified = tryParseDate(_data["modified"]);
            this.offlineEventType = _data["offlineEventType"];

            this.stateTransitionDate = tryParseDate(_data["stateTransitionDate"]);
            this.createdByWorkflowAction = _data["createdByWorkflowAction"] ? WorkflowActionLink.fromJS(_data["createdByWorkflowAction"]) : <any>undefined;
            super.initArray(this, "tags", _data["tags"]);

            // Special case for offline applications...
            this.tryInitOfflineId(_data);
        }

        return this;
    }

    static fromJS(data: any): Application {
        data = typeof data === 'object' ? data : {};
        let result = new Application();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["_id"] = { "$oid": this.id };
        data["version"] = this.version;
        data["formId"] = this.formId;
        data["formVersion"] = this.formVersion;
        data["formOrganisationId"] = this.formOrganisationId;
        data["organisationId"] = this.organisationId;
        data["organisationName"] = this.organisationName;
        data["applicationIdDisplay"] = this.applicationIdDisplay;
        data["applicationState"] = this.applicationState;
        if (Array.isArray(this.assignedTo)) {
            data["assignedTo"] = [];
            for (let item of this.assignedTo)
                data["assignedTo"].push(item.toJSON());
        }
        data["created"] = this.created && this.created.toMillis ? {
            "$date": this.created.toMillis(),
        } : <any>undefined;
        data["createdBy"] = this.createdBy ? this.createdBy.toJSON() : <any>undefined;
        data["modified"] = this.modified && this.modified.toMillis ? {
            "$date": this.modified.toMillis(),
        } : <any>undefined;
        data["modifiedBy"] = this.modifiedBy;
        data["stateTransitionDate"] = this.stateTransitionDate ? this.stateTransitionDate.toString() : <any>undefined;
        data["stateRepeatCount"] = this.stateRepeatCount;
        data["draft"] = this.draft;
        data["useFormVersion"] = this.useFormVersion;
        data["createdByWorkflowAction"] = this.createdByWorkflowAction ? this.createdByWorkflowAction.toJSON() : <any>undefined;
        data["concurrentRepeaterEditingField"] = this.concurrentRepeaterEditingField;
        data["data"] = this.data;

        data["isOffline"] = this.isOffline;
        data["isDirty"] = !!this.isDirty;
        data["offlineApplicationId"] = this.offlineApplicationId;
        data["offlineEventType"] = this.offlineEventType;
        // data["data"] = this.data ? this.data.toJSON() : <any>undefined;
        return data;
    }

    parseMetadataObject(prefix: string, keepId: boolean) {
        let applicationMetadata = cloneDeep(this);
        delete applicationMetadata.data;

        let meta = this.internalParseMetadataObject({}, applicationMetadata, prefix, keepId);
        meta['Meta_ApplicationId'] = this.id ? this.id : '';
        meta['ApplicationId'] = this.id ? this.id : '';
        meta['ApplicationIdDisplay'] = this.applicationIdDisplay;
        meta['FormId'] = this.formId;
        meta['FormVersion'] = this.formVersion;

        return meta;
    }

    private tryInitOfflineId(_data: any) {
        const getOfflineDisplayId = (id: string) => {
            return offlineId?.startsWith(Application.OFFLINE_PREFIX)
                ? Application.OFFLINE_PREFIX + (new CRC32()).calculate(id).toString()
                : undefined;
        }

        let offlineId: string = this.offlineApplicationId;
        this.isOffline = offlineId?.startsWith(Application.OFFLINE_PREFIX) ? true : false;

        if (offlineId && offlineId?.startsWith(Application.OFFLINE_PREFIX) && !this.id) {
            this.id = offlineId;
        }

        if (offlineId && !this.offlineApplicationId) {
            this.offlineApplicationId = offlineId;
        }

        let offlineDisplayId = getOfflineDisplayId(offlineId);
        if (offlineDisplayId) {
            if (!this.applicationIdDisplay) {
                this.applicationIdDisplay = offlineDisplayId;
            }

            if (!this.tags.includes('offline')) {
                this.tags.push('offline');
            }
        }
    }

    private internalParseMetadataObject(meta, inputObject, prefix, keepId) {
        meta = meta || {};
        prefix = pascalCase(prefix || '') + (prefix ? '_' : '');
        const metaPrefix = 'Meta_' + prefix;

        let key, newKey;

        for (key in inputObject) {
            if (Object.hasOwn(inputObject, key)) {
                if (key === "id" && !keepId) {
                    continue;
                }

                let value = inputObject[key] || '';
                newKey = metaPrefix + pascalCase(key);
                meta[newKey] = value;

                if (isObject(value)) {
                    delete meta[newKey];

                    if (Object.hasOwn(value, '$oid')) {
                        meta[newKey] = value['$oid'];
                        continue;
                    }

                    if (Object.hasOwn(value, '$date')) {
                        // meta[newKey] = new Date(value.$date).toLocaleString();
                        meta[newKey] = new Date(value['$date']).toFormattedString("dd/MM/yyyy h:mm:ss tt");
                        continue;
                    }

                    if (Object.hasOwn(value, 'isLuxonDateTime') && value['isLuxonDateTime'] == true) {
                        meta[newKey] = (<DateTime>value).toFormat('dd/MM/yyyy h:mm:ss a');
                        // this.internalParseMetadataObject(meta, value, prefix + pascalCase(key), false);
                        continue;
                    }

                    this.internalParseMetadataObject(meta, value, prefix + pascalCase(key), false);
                }
            }
        }

        return meta;
    }
}

/** An iApply Application object with metadata. */
export interface IApplication {
    id: string | undefined;
    /** Gets or sets the application version number. */
    version: number;
    /** Gets or sets the form id. */
    formId: string | undefined;
    /** Gets or sets the form version. */
    formVersion: number | undefined;
    /** Gets or sets the form organisation. */
    formOrganisationId: string | undefined;
    /** Gets or sets the organisation id. */
    organisationId: string | undefined;
    /** Gets or sets the organisation name. */
    organisationName: string | undefined;
    /** Gets or sets the application id for display. */
    applicationIdDisplay: string | undefined;
    /** Gets or sets the application status. */
    applicationState: string | undefined;
    /** Gets or sets the application assignee (user id). */
    assignedTo: Assignee[] | undefined;
    /** Creation time of this entity. */
    created: DateTime | undefined;
    /** Creator of this entity. */
    createdBy: CreatedApplicationUser;
    /** Last modification date of this entity. */
    modified: DateTime | undefined;
    /** Last modifier user of this entity. */
    modifiedBy: string | undefined;
    /** Gets or sets the date/time the application transitioned to the current state. */
    stateTransitionDate: DateTime;
    /** Gets or sets the current state repeat counter.
        Use 1 as default for backward compatibility with existing applications. */
    stateRepeatCount: number | undefined;
    /** Gets or sets a value indicating whether the application is a draft. */
    draft: boolean;
    /** Gets or sets a value indicating whether an application should be tied to a
        specific version of a form's metadata. */
    useFormVersion: boolean;
    createdByWorkflowAction: WorkflowActionLink;
    /** Gets or sets the concurrent repeater editing field. */
    concurrentRepeaterEditingField: string | undefined;
    /** Application Data! */
    data: any; // data: DataDictionary;
    /** Tags (new 26.2) */
    tags: string[];
}

/** the base application user */
export class ApplicationBaseUser implements IApplicationBaseUser {
    type!: ApplicationUserType;
    sessionToken: string | undefined;
    identityUserId!: string | undefined;
    displayName!: string | undefined;
    emailAddress!: string | undefined;
    accountType!: AccountType;
    readonly isExternalUser!: boolean;
    externalUserDetails!: IExternalUserDetails;
    /** Provides a dictionary of key/value pairs used to extend the definition of an object. */
    extendedProperties!: { [key: string]: string; } | undefined;
    externalId!: string | undefined;
    /** Is this entity Deleted? */
    isDeleted!: boolean;
    /** Which user deleted this entity? */
    deleterUserId!: string | undefined;
    /** Deletion time of this entity. */
    deletionTime!: DateTime | undefined;
    /** Last modification date of this entity. */
    lastModificationTime!: DateTime | undefined;
    /** Last modifier user of this entity. */
    lastModifierUserId!: string | undefined;
    /** Creation time of this entity. */
    creationTime!: DateTime;
    /** Creator of this entity. */
    creatorUserId!: string | undefined;
    id!: string | undefined;

    constructor(data?: IApplicationBaseUser) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            // this.id = _data["_id"]['$oid'];
            trySetProp(this, _data, "Type");
            trySetProp(this, _data, "SessionToken");
            // this.identityUserId = _data["identityUserId"];
            trySetProp(this, _data, "DisplayName");
            trySetProp(this, _data, "EmailAddress");
            trySetProp(this, _data, "AccountType");
            // (<any>this).isExternalUser = _data["isExternalUser"];
            // this.externalUserDetails = _data["externalUserDetails"] ? IExternalUserDetails.fromJS(_data["externalUserDetails"]) : <any>undefined;
            if (_data["ExtendedProperties"]) {
                this.extendedProperties = {} as any;
                for (let key in _data["ExtendedProperties"]) {
                    if (_data["ExtendedProperties"].hasOwnProperty(key))
                        (<any>this.extendedProperties)![key] = _data["ExtendedProperties"][key];
                }
            }
            trySetProp(this, _data, "ExternalId");
            trySetProp(this, _data, "IsDeleted");
            trySetProp(this, _data, "DeleterUserId");
            this.creationTime = tryParseDate(_data["CreationTime"]); // _data["CreationTime"] ? DateTime.fromISO(_data["creationTime"].toString()) : <any>undefined;
            this.lastModificationTime = tryParseDate(_data["LastModificationTime"]); // _data["LastModificationTime"] ? DateTime.fromISO(_data["LastModificationTime"].toString()) : <any>undefined;
            this.deletionTime = tryParseDate(_data["DeletionTime"]);  //_data["DeletionTime"] ? DateTime.fromISO(_data["DeletionTime"].toString()) : <any>undefined;
            trySetProp(this, _data, "LastModifierUserId");
            trySetProp(this, _data, "CreatorUserId");
        }
    }

    static fromJS(data: any): ApplicationBaseUser {
        data = typeof data === 'object' ? data : {};
        let result = new ApplicationBaseUser();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["type"] = this.type;
        data["identityUserId"] = this.identityUserId;
        data["displayName"] = this.displayName;
        data["emailAddress"] = this.emailAddress;
        data["accountType"] = this.accountType;
        data["isExternalUser"] = this.isExternalUser;
        data["externalUserDetails"] = this.externalUserDetails ? this.externalUserDetails.toJSON() : <any>undefined;
        if (this.extendedProperties) {
            data["extendedProperties"] = {};
            for (let key in this.extendedProperties) {
                if (this.extendedProperties.hasOwnProperty(key))
                    (<any>data["extendedProperties"])[key] = (<any>this.extendedProperties)[key];
            }
        }
        data["externalId"] = this.externalId;
        data["isDeleted"] = this.isDeleted;
        data["deleterUserId"] = this.deleterUserId;
        data["deletionTime"] = this.deletionTime ? this.deletionTime.toString() : <any>undefined;
        data["lastModificationTime"] = this.lastModificationTime ? this.lastModificationTime.toString() : <any>undefined;
        data["lastModifierUserId"] = this.lastModifierUserId;
        data["creationTime"] = this.creationTime ? this.creationTime.toString() : <any>undefined;
        data["creatorUserId"] = this.creatorUserId;
        data["id"] = this.id;
        return data;
    }
}

/** the base application user */
export interface IApplicationBaseUser {
    type: ApplicationUserType;
    sessionToken: string | undefined;
    identityUserId: string | undefined;
    displayName: string | undefined;
    emailAddress: string | undefined;
    accountType: AccountType;
    isExternalUser: boolean;
    externalUserDetails: IExternalUserDetails;
    /** Provides a dictionary of key/value pairs used to extend the definition of an object. */
    extendedProperties: { [key: string]: string; } | undefined;
    externalId: string | undefined;
    /** Is this entity Deleted? */
    isDeleted: boolean;
    /** Which user deleted this entity? */
    deleterUserId: string | undefined;
    /** Deletion time of this entity. */
    deletionTime: DateTime | undefined;
    /** Last modification date of this entity. */
    lastModificationTime: DateTime | undefined;
    /** Last modifier user of this entity. */
    lastModifierUserId: string | undefined;
    /** Creation time of this entity. */
    creationTime: DateTime;
    /** Creator of this entity. */
    creatorUserId: string | undefined;
    id: string | undefined;
}

export class CreatedApplicationUser extends CommonBaseModel implements ICreatedApplicationUser {
    id: string;
    type: ApplicationUserType = ApplicationUserType.Anonymous;
    displayName: string = '';
    emailAddress: string = '';
    externalId: string = null;
    extendedProperties: { [key: string]: string; } = {};

    // init(_data?: any): this {
    //     super.init(_data);
    //     if (_data['ExtendedProperties']) {
    //         this.extendedProperties = _data['ExtendedProperties'];
    //     }
    //     return this;
    // }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["id"] = this.id;
        data["type"] = this.type;
        data["displayName"] = this.displayName;
        data["emailAddress"] = this.emailAddress;
        data["externalId"] = this.externalId;
        data["extendedProperties"] = this.extendedProperties;
        return data;
    }
}

export interface ICreatedApplicationUser {
    id: string;
    type: ApplicationUserType;
    displayName: string;
    emailAddress: string;
    externalId: string;
    extendedProperties: { [key: string]: string; } | undefined;
}

/** The application assignee. */
export class Assignee extends CommonBaseModel implements IAssignee {
    /** Gets or sets the application assignee user id. */
    id!: string | undefined;
    /** Gets or sets the application assignee display name. */
    name!: string | undefined;
    /** Gets or sets a value indicating whether assignee submitted application. */
    submitted!: boolean | undefined;

    constructor(data?: IAssignee) {
        super();
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any): this {
        super.init(_data);

        if (_data) {
            trySetProp(this, _data, "id");
            trySetProp(this, _data, "name");
            trySetProp(this, _data, "submitted");
        }

        return this;
    }

    static fromJS(data: any): Assignee {
        data = typeof data === 'object' ? data : {};
        let result = new Assignee();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["id"] = this.id;
        data["name"] = this.name;
        data["submitted"] = this.submitted;
        return data;
    }
}

/** The application assignee. */
export interface IAssignee {
    /** Gets or sets the application assignee user id. */
    id: string | undefined;
    /** Gets or sets the application assignee display name. */
    name: string | undefined;
    /** Gets or sets a value indicating whether assignee submitted application. */
    submitted: boolean | undefined;
}

/** A dictionary object where the type of the dictionary value could be a single instance or an array of System.Strings, a single instance or an array of value-types, or an array of System.Collections.Generic.Dictionary`2s. */
export class DataDictionary implements IDataDictionary {
    comparer!: IEqualityComparerOfString;
    readonly count!: number;
    readonly keys!: string[] | undefined;
    readonly values!: any[] | undefined;

    constructor(data?: IDataDictionary) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.comparer = _data["comparer"] ? IEqualityComparerOfString.fromJS(_data["comparer"]) : <any>undefined;
            (<any>this).count = _data["count"];
            if (Array.isArray(_data["keys"])) {
                (<any>this).keys = [] as any;
                for (let item of _data["keys"])
                    (<any>this).keys!.push(item);
            }
            if (Array.isArray(_data["values"])) {
                (<any>this).values = [] as any;
                for (let item of _data["values"])
                    (<any>this).values!.push(item);
            }
        }
    }

    static fromJS(data: any): DataDictionary {
        data = typeof data === 'object' ? data : {};
        let result = new DataDictionary();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["comparer"] = this.comparer ? this.comparer.toJSON() : <any>undefined;
        data["count"] = this.count;
        if (Array.isArray(this.keys)) {
            data["keys"] = [];
            for (let item of this.keys)
                data["keys"].push(item);
        }
        if (Array.isArray(this.values)) {
            data["values"] = [];
            for (let item of this.values)
                data["values"].push(item);
        }
        return data;
    }
}

/** A dictionary object where the type of the dictionary value could be a single instance or an array of System.Strings, a single instance or an array of value-types, or an array of System.Collections.Generic.Dictionary`2s. */
export interface IDataDictionary {
    comparer: IEqualityComparerOfString;
    count: number;
    keys: string[] | undefined;
    values: any[] | undefined;
}

export class IEqualityComparerOfString implements IIEqualityComparerOfString {

    constructor(data?: IIEqualityComparerOfString) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
    }

    static fromJS(data: any): IEqualityComparerOfString {
        data = typeof data === 'object' ? data : {};
        let result = new IEqualityComparerOfString();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        return data;
    }
}

export interface IIEqualityComparerOfString {
}

export class Qualifier {
    paths: ControlPath[] = [];

    constructor(qualifierString?: string) {
        if (qualifierString) {
            const parser = /(?<name>[^[]+)(?:\[(?<index>\d+)\])?/i;
            const paths = qualifierString.split('.');
            paths.forEach(selector => {
                let matches = parser.exec(selector).groups;
                this.addPath(matches['name'], matches['index'] ? +matches['index'] : null);
            })
        }
    }

    get length() {
        return this.paths.length;
    }

    get currentPath(): ControlPath {
        return this.paths[this.paths.length - 1];
    }

    /** Appends a new child path to the end of the array. */
    push(path: ControlPath) {
        this.paths.push(path);
    }

    /**
     * Removes and returns the last ControlPath item from the path array.
     * If the path array is empty, returns undefined and the array is not modified.
     */
    pop(): ControlPath | undefined {
        return this.paths.pop();
    }

    /** Inserts a new parent path at the start of the path array. */
    unshift(path: ControlPath) {
        this.paths.unshift(path);
    }

    /**
     * Removes and returns the first ControlPath item from the path array.
     * If the path array is empty, returns undefined and the array is not modified.
     */
    shift(): ControlPath | undefined {
        return this.paths.shift();
    }

    /**
     * Creates a new Controlpath and pushed it to the and of the path array.
     */
    addPath(controlName: string, index: number): this {
        this.paths.push(new ControlPath(controlName, index));
        return this;
    }

    /**
     * Find Controlpath specific to the repeater name specified.
     * @param repeaterName The name of the repeater control to search for in the path.
     * @returns A matching ControlPath.
     */
    findPath(repeaterName: string): ControlPath {
        let result = this.paths.find(path => path.name == repeaterName);
        return result;
    }

    /** Magic Method as well as used in qualifier data strings. */
    toString(): string {
        return this.paths.map(path => path.toString()).join('.');
    }

    fullPathString(controlName: string): string {
        if (hasValue(controlName)) {
            const path = this.paths.map(path => path.toString());
            return [...path, controlName].join('.');
        }

        return this.toString();
    }

    safeControlName(controlName: string): string {
        let path = this.fullPathString(controlName);
        return path.replace(/\]/g, '-').replace(/\[/g, '-').replace(/\./g, '');
    }

    clone(): Qualifier {
        let newQual = new Qualifier();
        this.paths?.forEach(element => {
            newQual.push(element.clone());
        });
        return newQual;
    }

    createChild(controlName: string, index: number): Qualifier {
        return new Qualifier(this.toString()).addPath(controlName, index);
    }
}

export function isQualifier(qualifier: any): qualifier is Qualifier {
    return qualifier && Object.hasOwn(qualifier, 'paths') && Array.isArray(qualifier.paths);
}

export function getQualifier(container: DataContainer, create: boolean = false): Qualifier {
    let qualifier = container.$$qualifier ?? <Qualifier>(container.__proxyObj?.path) ??  new Qualifier();
    if (create && !Object.hasOwn(container, '$$qualifier')) {
        container.$$qualifier = qualifier;
    }

    return qualifier;
}

export class ControlPath {
    name: string;
    index?: number | null;

    constructor(name: string, index?: number | null) {
        this.name = name;
        this.index = index ?? null;
    }

    toString(): string {
        return this.name + (this.index != null && !isNaN(this.index) ? '[' + this.index + ']' : '');
    }

    clone(): ControlPath {
        return new ControlPath(this.name, this.index);
    }
}

export interface WatchedField {
    fieldName: string;
    path: Qualifier;
}

export interface WatchedControl {
    fieldName: string;
    control: iApplyFormControl,
    path: Qualifier,
    subProperty: string,
    aggregateType?: 'Total' | 'RowCount',
    aggregateQualifier?: Qualifier,
    aggregateRepeaterName?: string
}

